import { Modal } from 'react-bootstrap'
import { useIsMutating } from 'react-query'
import { useFormik } from 'formik'
import { KTSVG } from '../../../_metronic/helpers'
import Select from 'react-select'
import * as Yup from 'yup'
import { selectCustomStyles } from '../../../utils/Select'
import { graphqlRequestClient, queryClient } from '../../../queries/client'
import { AddEeFilterMutation, FilterType, useAddEeFilterMutation, useGetEeFiltersQuery } from '../../../generated/graphql'
import Toast, { ToastType } from '../../../utils/toast'
import { filterTypeLV } from '../../../types/exitEnabler'
import { validateFormula } from './EEFilters'

type Props = {
  showAddModal: boolean
  toggleAddModal: () => void
}

function AddEEFilter({ showAddModal, toggleAddModal }: Props) {
  const addForm = useFormik({
    initialValues: {
      id: -1,
      name: '',
      group: '',
      type: '',
      formula: '',
    },
    validationSchema: Yup.object().shape(
      {
        name: Yup.string().trim().required('Enter filter name').min(3, 'Name must be at least 3 characters'),
        group: Yup.string().trim().required('Enter filter group').min(3, 'Group must be at least 3 characters'),
        type: Yup.string().required('Select filter type'),
        formula: Yup.string()
          .required('Enter formula (bodmas rules apply)')
          .when(['formula'], {
            is: (f: string) => f && !validateFormula(f),
            then: Yup.string().min(100, 'Invalid formula, must follow bodmas rules'),
          }),
      },
      [['formula', 'formula']]
    ),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: function (values) {
      const addData = {
        name: values.name.trim(),
        group: values.group.trim(),
        type: values.type as FilterType,
        formula: values.formula,
      }

      addMutate.mutate({ input: addData })
    },
  })

  const addMutate = useAddEeFilterMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (data: AddEeFilterMutation) => {
        queryClient.invalidateQueries(useGetEeFiltersQuery.getKey())
        addForm.resetForm()
        Toast(data.addEEFilter.message, ToastType.success)
        toggleAddModal()
      },
      onError: (error: any) => Toast(error.response.errors[0].message, ToastType.error),
    },
    {}
  )

  function handleCancel() {
    addForm.resetForm()
    toggleAddModal()
  }

  return (
    <Modal id='kt_modal_add_filter' aria-hidden='true' show={showAddModal} onHide={toggleAddModal}>
      <form onSubmit={addForm.handleSubmit}>
        <Modal.Header className='p-2'>
          <Modal.Title className='ps-5'>Add Filter</Modal.Title>
          <button
            className='btn btn-sm btn-icon btn-active-light-primary'
            type='button'
            data-bs-toggle='tooltip'
            title='Close'
            onClick={toggleAddModal}
          >
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className='mh-400px scroll-y me-n7 pe-7'>
            <div className='fv-row mb-6 col-12 pe-4'>
              <label className='form-label required'>Name</label>
              <input type='text' className='form-control' placeholder='Enter name' {...addForm.getFieldProps('name')} />
              <div className='text-danger mt-2'>
                {addForm.touched.name && addForm.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{addForm.errors.name}</div>
                  </div>
                )}
              </div>
            </div>
            <div className='fv-row mb-6 col-12 pe-4'>
              <label className='form-label required'>Group</label>
              <input type='text' className='form-control' placeholder='Enter group' {...addForm.getFieldProps('group')} />
              <div className='text-danger mt-2'>
                {addForm.touched.group && addForm.errors.group && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{addForm.errors.group}</div>
                  </div>
                )}
              </div>
            </div>
            <div className='mb-4'>
              <label className='form-label required'>Formula</label>
              <input type='text' className='form-control' placeholder='Enter formula' {...addForm.getFieldProps('formula')} />
              <div className='text-danger mt-2'>
                {addForm.touched.formula && addForm.errors.formula && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{addForm.errors.formula}</div>
                  </div>
                )}
              </div>
            </div>
            <div className='mb-4'>
              <label className='form-label required'>Type</label>
              <Select
                isSearchable={false}
                className='text-primary'
                options={filterTypeLV}
                name='type'
                onChange={(e: any) => addForm.setFieldValue('type', e.value)}
                styles={selectCustomStyles}
              />
              <div className='text-danger mt-2'>
                {addForm.touched.type && addForm.errors.type && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{addForm.errors.type}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='p-2'>
          <div className='d-flex justify-content-between'>
            <button type='button' className='btn btn-sm btn-secondary me-3' onClick={handleCancel}>
              Cancel
            </button>
            <button type='submit' className='btn btn-sm btn-primary' disabled={useIsMutating() > 0}>
              {useIsMutating() === 0 && 'Add Filter'}
              {useIsMutating() > 0 && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default AddEEFilter
